/**
 * DrillDetailView - Full-screen read-only drill record detail view with audit trail
 *
 * MCA Compliance: ISM Code Element 9, WBC3 SMS requirements
 * Shows full drill record details with audit history for inspections
 *
 * Converted from DrillDetailModal to match Equipment Register full-screen pattern
 */

import React, { useState, useCallback } from 'react';
import {
  Calendar,
  Clock,
  Users,
  User,
  MapPin,
  CheckCircle,
  XCircle,
  FileText,
  AlertTriangle,
  History,
  Edit3,
  ChevronDown,
  ChevronUp,
  Loader2,
  Trash2,
  ShieldCheck,
  Lock,
  X
} from 'lucide-react';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
// FT-403: mobile bottom bar action slot — sign-off button lives here on mobile.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import { Badge } from '../../../catalyst/badge';
import { getDrillDisplayName } from './utils/drillsHelpers';
import { formatUserIdentity } from '../../../../utils/userDisplay';
import EditReasonModal from '../../../common/EditReasonModal';
import { getCreatedByName } from '../../shared/AttributionBadge';
import { usePermissions } from '../../../../hooks/usePermissions';

const DrillDetailView = ({
  drill,
  onBack,
  onEdit,
  onDelete,
  isDeleting = false,
  onSignOff  // FT-06d P2: Sign-off callback
}) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  const [showEditModal, setShowEditModal] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(true);
  const [isSigningOff, setIsSigningOff] = useState(false);
  // FT-403: confirmation dialog before sign-off fires.
  const [showSignOffConfirm, setShowSignOffConfirm] = useState(false);

  // FT-394 FIX B: use the RBAC gate (includes vessel-assignment check) instead of raw role checks
  const { can } = usePermissions();
  const canSignOff = can('sign', 'drills');
  const isSignedOff = drill?.reviewStatus === 'approved';
  // FT-394: pending = record created but not yet signed off (now the default for all new drills)
  const isAwaitingSignOff = drill?.reviewStatus === 'pending' && !isSignedOff;

  // FT-91: Normalize outcome for case-insensitive comparison
  const normalizeOutcome = (outcome) => {
    if (!outcome) return 'failed';
    const normalized = outcome.toLowerCase();
    if (normalized === 'successful' || normalized === 'success' || normalized === 'passed' || normalized === 'pass') {
      return 'successful';
    }
    return 'failed';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format timestamp for audit
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle edit button click
  const handleEditClick = () => {
    setShowEditModal(true);
  };

  // Handle edit confirmation with reason
  const handleEditConfirm = (reason) => {
    setShowEditModal(false);
    if (onEdit) {
      onEdit(drill, reason);
    }
  };

  // FT-403: open the confirmation dialog (called by both header button and bottom bar).
  const handleRequestSignOff = useCallback(() => {
    if (!onSignOff || isSigningOff || isSignedOff) return;
    setShowSignOffConfirm(true);
  }, [onSignOff, isSigningOff, isSignedOff]);

  // FT-06d P2 / FT-403: Handle sign-off — fires AFTER the confirmation dialog confirms.
  const handleSignOff = async () => {
    setShowSignOffConfirm(false);
    if (!onSignOff || isSigningOff) return;
    setIsSigningOff(true);
    try {
      await onSignOff();
    } finally {
      setIsSigningOff(false);
    }
  };

  // FT-403: wire Sign Off into the mobile bottom bar (middle slot) so it isn't
  // crammed into the header on small screens. The header button stays for desktop
  // (`hidden lg:inline-flex` keeps it off mobile where the bottom bar shows it).
  useSetBottomBarActions(
    canSignOff && onSignOff && !isSignedOff
      ? [
          {
            key: 'drills-sign-off',
            icon: ShieldCheck,
            label: isSigningOff ? 'Signing…' : 'Sign Off',
            primary: true,
            disabled: isSigningOff || isDeleting,
            onClick: handleRequestSignOff,
          },
        ]
      : []
  );

  if (!drill) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Drill record not found</p>
      </div>
    );
  }

  // FT-91: Use normalizeOutcome for case-insensitive comparison
  const normalizedOutcome = normalizeOutcome(drill.outcome);
  const OutcomeIcon = normalizedOutcome === 'successful' ? CheckCircle : XCircle;
  const outcomeColor = normalizedOutcome === 'successful' ? 'green' : 'red';

  // Get participant names
  const participantNames = drill.participants?.map(p => {
    if (typeof p === 'string') return p;
    return p.name || `${p.firstName || ''} ${p.surname || ''}`.trim() || 'Unknown';
  }).filter(Boolean) || [];

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gray-50"
    >
      <SwipeIndicator progress={swipeProgress} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Drill Record</h1>
                <p className="text-sm text-gray-500">View details and audit trail</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* FT-394 FIX D: only skipper+ can delete a pending drill (preserves audit trail) */}
              {onDelete && canSignOff && !isSignedOff && (
                <button
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
              {onEdit && !isSignedOff && (
                <button
                  onClick={handleEditClick}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
              )}
              {/* FT-06d P2 / FT-403: Sign Off button — skipper+ only, desktop only.
                  On mobile this button is in the bottom bar (useSetBottomBarActions above).
                  `hidden lg:inline-flex` keeps it off mobile where the bottom bar shows it. */}
              {!isSignedOff && canSignOff && onSignOff && (
                <button
                  onClick={handleRequestSignOff}
                  disabled={isSigningOff || isDeleting}
                  className="hidden lg:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSigningOff ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {isSigningOff ? 'Signing...' : 'Sign Off'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto p-6 space-y-6">

        {/* FT-394: sign-off status indicators */}
        {isSignedOff && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
            <Lock className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">
              {/* FT-06e: Show "Name (Role)" format */}
              Drill signed off by {formatUserIdentity(drill.reviewedBy, drill.reviewedByRole)} on {formatTimestamp(drill.reviewedAt)}
            </span>
          </div>
        )}
        {/* FT-394: show awaiting banner so crew know the record is pending skipper review */}
        {isAwaitingSignOff && (
          <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">
              Awaiting sign-off — a skipper or admin needs to review and sign off this drill record.
            </span>
          </div>
        )}

        {/* Section 1: Drill Information */}
        <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white">Drill Information</h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Drill Type</dt>
                <dd className="mt-1 text-sm text-gray-900 font-medium">{getDrillDisplayName(drill.type)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Date</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDate(drill.date)}
                </dd>
              </div>
              {drill.time && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Time</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    {formatTime(drill.time)}
                  </dd>
                </div>
              )}
              {drill.duration && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Duration</dt>
                  <dd className="mt-1 text-sm text-gray-900">{drill.duration} minutes</dd>
                </div>
              )}
              {drill.location && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {drill.location}
                  </dd>
                </div>
              )}
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Outcome</dt>
                <dd className="mt-1">
                  <Badge color={outcomeColor}>
                    <OutcomeIcon className="h-3 w-3 mr-1" />
                    {normalizedOutcome === 'successful' ? 'Successful' : 'Failed'}
                  </Badge>
                </dd>
              </div>
            </dl>
          </div>
        </section>

        {/* Section 2: Participants */}
        {participantNames.length > 0 && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Users className="h-4 w-4 text-white" />
                Participants ({participantNames.length})
              </h2>
            </div>
            <div className="p-6">
              <ul className="divide-y divide-gray-100">
                {participantNames.map((name, index) => (
                  <li key={index} className="py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-cyan-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Section 3: Notes */}
        {drill.notes && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <FileText className="h-4 w-4 text-white" />
                Notes
              </h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{drill.notes}</p>
            </div>
          </section>
        )}

        {/* Section 4: Deficiencies (for failed drills) */}
        {drill.outcome === 'failed' && drill.deficiencies && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-red-50 px-5 py-3">
              <h2 className="text-base font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Deficiencies Identified
              </h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{drill.deficiencies}</p>
            </div>
          </section>
        )}

        {/* Section 5: Corrective Actions (for failed drills) */}
        {drill.outcome === 'failed' && drill.correctiveActions && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-amber-50 px-5 py-3">
              <h2 className="text-base font-semibold text-amber-800 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Corrective Actions
              </h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{drill.correctiveActions}</p>
            </div>
          </section>
        )}

        {/* Section 6: Audit Trail - Compact scannable format */}
        <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <button
            onClick={() => setAuditExpanded(!auditExpanded)}
            className="w-full bg-slate-700 px-5 py-3 flex items-center justify-between hover:bg-slate-500 transition-colors"
          >
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <History className="h-4 w-4 text-white" />
              Audit Trail
            </h2>
            {auditExpanded ? (
              <ChevronUp className="h-5 w-5 text-white" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white" />
            )}
          </button>
          {auditExpanded && (
            <div className="px-6 py-4 space-y-3 text-sm max-h-96 overflow-y-auto">
              {drill.auditLog && drill.auditLog.length > 0 ? (
                drill.auditLog.map((entry, index) => (
                  <div key={entry.id || index} className="text-gray-700 pb-3">
                    <div className="font-medium">
                      • {entry.action === 'created' ? 'Created' : 'Edited'} by {entry.user} - {formatTimestamp(entry.timestamp)}
                    </div>
                    <div className="ml-4 text-gray-600 space-y-1">
                      {/* Summary */}
                      {entry.summary && entry.action !== 'created' && (
                        <div>{entry.summary}</div>
                      )}

                      {/* Field-level changes (if any) */}
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="ml-2 text-sm space-y-0.5">
                          {entry.changes.map((change, changeIndex) => (
                            <div key={changeIndex} className="text-gray-500">
                              • {change.field}: "{String(change.displayOld || change.oldValue || 'empty')}" → "{String(change.displayNew || change.newValue)}"
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reason */}
                      {entry.reason && entry.reason !== 'Initial record creation' && (
                        <div>Reason: {entry.reason}</div>
                      )}

                      {/* Vessel */}
                      {entry.vessel && (
                        <div>Vessel: {entry.vessel}</div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-700">
                  <div className="font-medium">
                    • Created by {getCreatedByName(drill.createdBy) || 'Unknown user'} - {drill.createdAt ? formatTimestamp(drill.createdAt) : formatDate(drill.date)}
                  </div>
                  <div className="ml-4 text-gray-600">
                    <div>Initial record creation</div>
                    {drill.vesselName && (
                      <div>Vessel: {drill.vesselName}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Record metadata footer */}
        <div className="text-center text-xs text-gray-400 py-4">
          Record ID: {drill.id}
          {drill.updatedAt && (
            <span className="ml-2">• Last updated: {formatTimestamp(drill.updatedAt)}</span>
          )}
          {drill.vesselName && (
            <span className="ml-2">• Vessel: {drill.vesselName}</span>
          )}
        </div>
      </div>

      {/* FT-403: Sign Off confirmation dialog — same pattern as MaintenanceCompletionReport. */}
      {showSignOffConfirm && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          data-testid="drills-signoff-confirm-dialog"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-25"
            onClick={() => setShowSignOffConfirm(false)}
          />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-green-50">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Sign off this record?
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSignOffConfirm(false)}
                  className="text-gray-400 hover:text-gray-500"
                  aria-label="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              {/* Body */}
              <div className="p-6">
                <p className="text-sm text-gray-700">
                  By confirming, you are signing off as a skipper or admin that you have reviewed
                  this entry. This action cannot be undone.
                </p>
              </div>
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowSignOffConfirm(false)}
                  data-testid="drills-signoff-cancel-btn"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSignOff}
                  data-testid="drills-signoff-confirm-btn"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 flex items-center gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reason Modal */}
      <EditReasonModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onConfirm={handleEditConfirm}
        item={drill}
        recordType="drill record"
      />
    </div>
  );
};

export default DrillDetailView;
